import React from 'react';
import { useForm } from 'react-hook-form';
import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import { Input, Select, Alert, Button } from '@v2/components';
import type { SchemaFormProps, FieldDefinition, GenericSchema } from './types';
import { styles } from './styles';

function renderField(
  field: FieldDefinition,
  register: ReturnType<typeof useForm>['register'],
  errors: Record<string, { message?: string }>
): React.ReactNode {
  const error = errors[field.name]?.message;

  switch (field.type) {
    case 'select':
      return (
        <Select
          {...register(field.name)}
          label={field.label}
          placeholder={field.placeholder}
          options={field.options ?? []}
          error={error}
          disabled={field.disabled}
          required
        />
      );

    case 'checkbox':
      return null;

    case 'text':
    case 'email':
    case 'textarea':
    default:
      return (
        <Input
          {...register(field.name)}
          label={field.label}
          placeholder={field.placeholder}
          type={field.type === 'textarea' ? 'text' : field.type}
          error={error}
          disabled={field.disabled}
          autoComplete={field.autoComplete}
          required
        />
      );
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bridge between valibot's BaseSchema generics and RHF's FieldValues; TS4.9 cannot infer the intersection
type ValibotResolverSchema = Parameters<typeof valibotResolver>[0];

export function SchemaForm<TSchema extends GenericSchema>({
  fields,
  schema,
  defaultValues,
  onSubmit,
  loading = false,
  error,
  columns = 1,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  onCancel,
  footer,
}: SchemaFormProps<TSchema>): React.ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FieldValues>({
    resolver: valibotResolver(schema as unknown as ValibotResolverSchema),
    defaultValues: defaultValues as Record<string, unknown>,
  });

  const isLoading = loading || isSubmitting;
  const gridClass = columns === 2 ? styles.grid2 : styles.grid1;
  const visibleFields = fields.filter((f) => !f.hidden);

  return (
    <form onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)} className={styles.form}>
      {error && <Alert variant="error">{error}</Alert>}

      <div className={gridClass}>
        {visibleFields.map((field) => {
          const span = field.colSpan === 2 && columns === 2;
          return (
            <div key={field.name} className={span ? styles.fullWidth : undefined}>
              {renderField(field, register, errors as Record<string, { message?: string }>)}
            </div>
          );
        })}
      </div>

      {footer ?? (
        <div className={styles.footer}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              {cancelLabel}
            </Button>
          )}
          <Button
            variant="primary"
            type="submit"
            disabled={!isDirty || isLoading}
            isLoading={isLoading}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
}

export type { SchemaFormProps, FieldDefinition, FieldType, GenericSchema } from './types';
export default SchemaForm;
